import { makeAutoObservable } from 'mobx'
import { jwtDecode } from 'jwt-decode'
import { i18nStore } from '@store/I18nStore'
import { clearCentrifugeTokenCache } from '@utils/centrifugeTokens'

/** Same key/marker as apiHelpers (not imported: apiHelpers imports this store — no cycle). */
const SESSION_EXPIRED_MESSAGE_KEY = 'auth.sessionExpiredMessage'
const SESSION_EXPIRED_MARKER = 'auth.session.expired'

export type User = {
  id: number
  email: string
  role?: string
  createdAt?: string
  avatarUrl?: string | null
  displayName?: string | null
  title?: string | null
  verifiedAt?: string | null
  emailVerified?: boolean
}

const USER_STORAGE_KEY = 'auth.user'

/**
 * Only these profile fields are kept in memory / localStorage. Whatever else
 * an API response carries (hashes, internal flags, tokens) is dropped here so
 * it can never end up persisted in the browser.
 */
const USER_FIELDS: ReadonlyArray<keyof User> = [
  'id',
  'email',
  'role',
  'createdAt',
  'avatarUrl',
  'displayName',
  'title',
  'verifiedAt',
  'emailVerified',
]

function pickUserFields(user: User | null): User | null {
  if (!user || typeof user !== 'object') return null
  const src = user as unknown as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const key of USER_FIELDS) {
    if (src[key] !== undefined) out[key] = src[key]
  }
  return out as unknown as User
}
const TOKEN_STORAGE_KEY = 'auth.token'

const EMPTY_AUTH_HEADER: Record<string, string> = {}

/** POST /api/auth/refresh: the browser sends the httpOnly refresh cookie, no body / Authorization. */
const REFRESH_PATH = '/api/auth/refresh'
/** Silent refresh this many seconds before the access token runs out (+ a little per-tab jitter). */
const REFRESH_AHEAD_SEC = 120
const EXPIRY_TICK_MS = 15_000
/** A refresh that just failed on the network / 5xx / 429 is not retried more often than this. */
const REFRESH_ERROR_BACKOFF_MS = 5_000
/** Tokens this tab has held recently — the fetch interceptor only swaps/refreshes those. */
const KNOWN_TOKENS_MAX = 6
const IN_APP_PATH_RE = /^\/(dialogs|visitors|control|team|myProfile|settings|statistics|billing)(\/|$)/

/**
 * Backend verdicts on POST /api/auth/refresh:
 *  ok       — new access token;
 *  expired  — REFRESH_INVALID / REFRESH_EXPIRED / REFRESH_REUSED / SESSION_REVOKED /
 *             SESSION_EXPIRED / TOKEN_REVOKED (or a bare 401/403): clear auth, show login;
 *  blocked  — ACCOUNT_BLOCKED: login page with the "account blocked" notice;
 *  error    — network / 429 / 5xx: nothing is decided, keep the session and retry later.
 */
export type RefreshResult = 'ok' | 'expired' | 'blocked' | 'error'

const SESSION_OVER_CODES = new Set([
  'REFRESH_INVALID',
  'REFRESH_EXPIRED',
  'REFRESH_REUSED',
  'SESSION_REVOKED',
  'SESSION_EXPIRED',
  'TOKEN_REVOKED',
  'TOKEN_EXPIRED',
  'UNAUTHORIZED',
])

function readCode(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const root = data as Record<string, unknown>
  const nested = root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : null
  const raw = root.code ?? nested?.code ?? root.error_code ?? root.reason
  return typeof raw === 'string' ? raw.trim().toUpperCase() : ''
}

function readMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const root = data as Record<string, unknown>
  const msg = typeof root.message === 'string' ? root.message.trim() : ''
  if (msg) return msg
  return typeof root.error === 'string' ? root.error.trim() : ''
}

function tokenExp(token: string | null): number | null {
  if (!token) return null
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token)
    return typeof exp === 'number' && Number.isFinite(exp) ? exp : null
  } catch {
    return null
  }
}

function tokenAlive(token: string | null): boolean {
  const exp = tokenExp(token)
  return exp != null && exp > Date.now() / 1000
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

function readEmailVerified(data: unknown): boolean | undefined {
  if (!data || typeof data !== 'object') return undefined
  const root = data as Record<string, unknown>
  const nested = root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : null
  const value = nested?.emailVerified ?? root.emailVerified
  return typeof value === 'boolean' ? value : undefined
}

export class AuthStore {
  currentUser: User | null = null
  accessToken: string | null = null
  /**
   * Page reload with a dead / missing access token: the refresh cookie decides whether the
   * session is still alive. Until it answers, the operator stays "signed in" (no login flash);
   * requests sent meanwhile wait for the refresh in the fetch interceptor.
   */
  sessionRestorePending = false
  /** Stable object identity until the token changes — safe as a React effect dependency. */
  private cachedAuthHeader: Record<string, string> = EMPTY_AUTH_HEADER
  private refreshInflight: Promise<RefreshResult> | null = null
  private lastRefreshErrorAt = 0
  private lastRefreshResult: RefreshResult | null = null
  private knownTokens: string[] = []
  /** Tokens of sessions that ended in this tab — the interceptor answers them with 401 locally, no network. */
  private deadTokens: string[] = []
  private readonly refreshAheadSec = REFRESH_AHEAD_SEC + Math.floor(Math.random() * 20)

  constructor() {
    makeAutoObservable(this, {
      refreshInflight: false,
      lastRefreshErrorAt: false,
      lastRefreshResult: false,
      knownTokens: false,
      deadTokens: false,
      refreshAheadSec: false,
      sessionResetHandlers: false,
    } as {
      refreshInflight: false
      lastRefreshErrorAt: false
      lastRefreshResult: false
      knownTokens: false
      deadTokens: false
      refreshAheadSec: false
      sessionResetHandlers: false
    })
    this.hydrate()
    this.restoreSessionIfNeeded()
    this.watchTokenExpiry()
    this.watchOtherTabs()
  }

  /** Seconds-to-live of the access token, or null when there is none / it is unreadable. */
  get tokenSecondsLeft(): number | null {
    const exp = tokenExp(this.accessToken)
    if (exp == null) return null
    return Math.round(exp - Date.now() / 1000)
  }

  // ------------------------------------------------------------------ refresh flow

  /** One refresh at a time: every caller (timer, interceptor, 401 retry) shares the in-flight request. */
  refreshAccessToken(): Promise<RefreshResult> {
    if (this.refreshInflight) return this.refreshInflight
    // Session already over (logged out / refresh said expired): nothing to refresh, no request.
    if (!this.currentUser) return Promise.resolve('expired')
    // A burst of 401s right after a network / 5xx failure must not hammer the endpoint.
    if (this.lastRefreshResult === 'error' && Date.now() - this.lastRefreshErrorAt < REFRESH_ERROR_BACKOFF_MS) {
      return Promise.resolve('error')
    }
    const run = this.doRefresh()
      .then((result) => {
        this.lastRefreshResult = result
        // A terminal verdict ends the session whoever asked (timer, restore, interceptor):
        // otherwise every later request would knock on /refresh again.
        if (result === 'expired' || result === 'blocked') this.afterRefresh(result)
        return result
      })
      .finally(() => {
        this.refreshInflight = null
      })
    this.refreshInflight = run
    return run
  }

  private async doRefresh(): Promise<RefreshResult> {
    const base = (i18nStore.apiBaseUrl || '').replace(/\/$/, '')
    if (!base || typeof fetch !== 'function') return 'error'
    let response: Response
    try {
      response = await fetch(`${base}${REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
      })
    } catch {
      this.noteRefreshError()
      return 'error'
    }
    let data: unknown = null
    try {
      data = await response.json()
    } catch {
      data = null
    }
    const token =
      data && typeof data === 'object' && typeof (data as { token?: unknown }).token === 'string'
        ? String((data as { token: string }).token).trim()
        : ''
    if (response.ok && token) {
      this.applyRefreshedToken(token, data)
      return 'ok'
    }
    const code = readCode(data)
    if (code === 'ACCOUNT_BLOCKED') {
      this.rememberLoginMessage(readMessage(data))
      return 'blocked'
    }
    if (code === 'AUTH_ORIGIN_FORBIDDEN') {
      // Allowed-origins misconfiguration: the session cannot be kept alive from this host —
      // say so on the login page with the backend's own wording.
      this.rememberLoginMessage(readMessage(data) || 'AUTH_ORIGIN_FORBIDDEN')
      return 'expired'
    }
    const sessionOver =
      SESSION_OVER_CODES.has(code) ||
      (!code && (response.status === 400 || response.status === 401 || response.status === 403))
    if (sessionOver) {
      // Another tab may have rotated the cookie a moment ago and already holds the new token.
      const stored = readStoredToken()
      if (stored && stored !== this.accessToken && tokenAlive(stored)) {
        this.applyRefreshedToken(stored)
        return 'ok'
      }
      return 'expired'
    }
    // 429 / 5xx / proxy / unknown body: not a verdict on the session — keep it and retry later.
    this.noteRefreshError()
    return 'error'
  }

  /** Login-page banner text (first reason wins) — same storage the login page already reads. */
  private rememberLoginMessage(message: string): void {
    const text = String(message ?? '').trim()
    if (!text) return
    try {
      if (!sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY)) {
        sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, text)
      }
    } catch {}
  }

  private rememberToken(token: string | null): void {
    if (!token) return
    this.knownTokens = [token, ...this.knownTokens.filter((t) => t !== token)].slice(0, KNOWN_TOKENS_MAX)
  }

  /** Bearer this tab issued at some point (current or rotated out) — never someone else's token. */
  isOwnToken(bearer: string): boolean {
    return bearer === this.accessToken || this.knownTokens.includes(bearer)
  }

  /** A token of a session that already ended in this tab — any request with it can only get 401. */
  isDeadToken(bearer: string): boolean {
    return this.deadTokens.includes(bearer)
  }

  /** New access token for the same session: no cache resets, the realtime connection stays. */
  applyRefreshedToken(token: string, data?: unknown): void {
    this.accessToken = token
    this.rememberToken(token)
    this.lastRefreshResult = 'ok'
    this.syncAuthHeader()
    const emailVerified = readEmailVerified(data)
    if (emailVerified !== undefined && this.currentUser && this.currentUser.emailVerified !== emailVerified) {
      this.currentUser = { ...this.currentUser, emailVerified }
    }
    this.sessionRestorePending = false
    this.lastRefreshErrorAt = 0
    this.persist()
  }

  private noteRefreshError(): void {
    this.lastRefreshErrorAt = Date.now()
  }

  /** Interceptor: this request carries a token that is dead or about to die — refresh first. */
  shouldRefreshBeforeRequest(bearer: string): boolean {
    if (!this.currentUser || !this.isOwnToken(bearer)) return false
    if (this.sessionRestorePending) return true
    if (bearer !== this.accessToken) return false
    const left = this.tokenSecondsLeft
    return left == null || left <= 5
  }

  /**
   * A 401 TOKEN_EXPIRED that reached the caller means the refresh did not help. Only treat it
   * as "session over" when the refresh really said so (expired / revoked / reused) — never on
   * network, 429 or 5xx, and not while the session is still being restored.
   */
  keepSessionDespite401(): boolean {
    if (this.sessionRestorePending || this.refreshInflight) return true
    return this.lastRefreshResult === 'error'
  }

  /** Reload: a dead or near-dead access token is replaced through the refresh cookie. */
  private restoreSessionIfNeeded(): void {
    if (!this.currentUser || typeof window === 'undefined') return
    const left = this.tokenSecondsLeft
    if (left != null && left > this.refreshAheadSec) return
    this.sessionRestorePending = !tokenAlive(this.accessToken)
    void this.refreshAccessToken()
  }

  private afterRefresh(result: RefreshResult): void {
    if (result === 'expired') this.endSessionExpired()
    else if (result === 'blocked') this.endSessionBlocked()
    // 'error': the timer keeps retrying; a dead token is retried by the interceptor on demand
  }

  /** ACCOUNT_BLOCKED on refresh — same landing as a blocked 403 on any API call. */
  private endSessionBlocked(): void {
    this.sessionRestorePending = false
    this.logout({ skipRemote: true })
    if (typeof window === 'undefined') return
    const path = '/login?reason=blocked'
    if (`${window.location.pathname}${window.location.search}` !== path) {
      window.location.replace(path)
    }
  }

  /** Session is over (refresh cookie gone): login page with a calm "session ran out" banner. */
  private endSessionExpired(): void {
    try {
      if (!sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY)) {
        sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, SESSION_EXPIRED_MARKER)
      }
    } catch {}
    this.sessionRestorePending = false
    this.logout({ skipRemote: true })
    if (typeof window === 'undefined') return
    if (IN_APP_PATH_RE.test(window.location.pathname)) {
      window.history.pushState({}, '', '/login')
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  /**
   * Silent refresh ~2 minutes before the access token runs out, plus a check whenever the tab
   * comes back (visibility / online) — a laptop that slept through the expiry recovers at once.
   */
  private watchTokenExpiry(): void {
    if (typeof window === 'undefined') return
    const tick = () => {
      if (!this.currentUser) return
      if (this.sessionRestorePending) {
        if (!this.refreshInflight) void this.refreshAccessToken()
        return
      }
      if (!this.accessToken || this.refreshInflight) return
      const left = this.tokenSecondsLeft
      if (left == null || left > this.refreshAheadSec) return
      void this.refreshAccessToken()
    }
    window.setInterval(tick, EXPIRY_TICK_MS)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') tick()
    })
    window.addEventListener('online', tick)
  }

  /** Another tab refreshed first: take its token instead of refreshing (and rotating) again. */
  private watchOtherTabs(): void {
    if (typeof window === 'undefined') return
    window.addEventListener('storage', (event) => {
      if (event.key !== TOKEN_STORAGE_KEY || !event.newValue) return
      if (event.newValue !== this.accessToken && this.currentUser && tokenAlive(event.newValue)) {
        this.applyRefreshedToken(event.newValue)
      }
    })
  }

  // ------------------------------------------------------------------ state

  private syncAuthHeader() {
    this.cachedAuthHeader = this.accessToken
      ? { Authorization: `Bearer ${this.accessToken}` }
      : EMPTY_AUTH_HEADER
  }

  private persist() {
    try {
      if (this.currentUser) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(this.currentUser))
      else localStorage.removeItem(USER_STORAGE_KEY)
      if (this.accessToken) localStorage.setItem(TOKEN_STORAGE_KEY, this.accessToken)
      else localStorage.removeItem(TOKEN_STORAGE_KEY)
    } catch {}
  }

  private hydrate() {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY)
      if (!raw) return
      this.currentUser = pickUserFields(JSON.parse(raw) as User)
      const t = localStorage.getItem(TOKEN_STORAGE_KEY)
      if (t) {
        this.accessToken = t
        this.rememberToken(t)
      }
      this.syncAuthHeader()
    } catch {}
  }

  setUser(user: User | null) {
    const prevId = this.currentUser?.id
    this.currentUser = pickUserFields(user)
    this.persist()
    if (prevId != null && user?.id !== prevId) {
      this.resetSessionCaches()
    }
  }

  setToken(token: string | null) {
    const prev = this.accessToken
    this.accessToken = token
    this.rememberToken(token)
    if (token) this.deadTokens = this.deadTokens.filter((t) => t !== token)
    this.sessionRestorePending = false
    this.lastRefreshResult = null
    this.syncAuthHeader()
    this.persist()
    if (prev && token !== prev) {
      this.resetSessionCaches()
    }
  }

  logout(opts?: { skipRemote?: boolean }) {
    const base = (i18nStore.apiBaseUrl || '').replace(/\/$/, '')
    // Cookie-based: works even after the access token ran out, so no token check here.
    if (!opts?.skipRemote && base && this.currentUser) {
      void fetch(`${base}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...this.authHeader },
      }).catch(() => {})
    }
    const ended = [...new Set([this.accessToken, ...this.knownTokens])].filter((t): t is string => Boolean(t))
    this.deadTokens = [...ended, ...this.deadTokens.filter((t) => !ended.includes(t))].slice(0, KNOWN_TOKENS_MAX)
    this.currentUser = null
    this.accessToken = null
    this.knownTokens = []
    this.lastRefreshResult = null
    this.sessionRestorePending = false
    this.syncAuthHeader()
    this.persist()
    this.resetSessionCaches()
  }

  /**
   * Per-session stores (billing access, email verification) register their reset here — the
   * root store wires them up, so this module never imports them (they import it).
   */
  private sessionResetHandlers = new Set<() => void>()

  onSessionReset(handler: () => void): () => void {
    this.sessionResetHandlers.add(handler)
    return () => {
      this.sessionResetHandlers.delete(handler)
    }
  }

  private resetSessionCaches() {
    clearCentrifugeTokenCache()
    for (const handler of this.sessionResetHandlers) {
      try {
        handler()
      } catch {
        /* one store failing to reset must not block the others */
      }
    }
  }

  get isAuthenticated(): boolean {
    if (!this.currentUser) return false
    if (this.sessionRestorePending) return true
    return tokenAlive(this.accessToken)
  }

  get authHeader(): Record<string, string> {
    return this.cachedAuthHeader
  }
}

export const authStore = new AuthStore()
